import { useMember } from "api/members";
import { CustomMemberListSelect } from "components/FormControls/CustomMemberListSelect";
import { maxVisibleConditionDataCount } from "components/Layout/Segment/SegmentForm/SegmentForm";
import { MoreElements } from "components/MoreElements/MoreElements";
import { Field } from "formik";
import { useMessage } from "hooks/intl";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { ListResponse } from "types/listing";
import { BackendMemberModel } from "types/members";
import { CustomMemberList as CustomMemberListType } from "types/segments";
import { LabelValuePair } from "utilities/objects";
import { MemberFetcher } from "./MemberFetcher";
import { useStyles } from "./SegmentConditions.styles";

export const initialValuesCustomMemberList: CustomMemberListType = {
  customers: [],
  type: "customer_list",
};

export function CustomMemberList({
  formField,
  values,
  readOnly,
}: {
  formField: string;
  values: any;
  readOnly?: boolean;
}) {
  const classes = useStyles();
  const { message } = useMessage();

  const [searchedMemberValue, setSearchedMemberValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [listOfFetchedMembers, setListOfFetchedMembers] = useState<
    BackendMemberModel[]
  >([]);
  const { useMembersList } = useMember();

  const { data: searchedMembers } = useMembersList({
    filters: searchedMemberValue
      ? [
          {
            condition: "",
            query: "identifier",
            value: searchedMemberValue,
          },
        ]
      : [],
  }) as {
    data: ListResponse;
  };

  const field = get(values, formField);
  const fieldName = `${formField}.customers`;
  const customersArray = get(values, fieldName);
  const formData = customersArray;

  const getMemberItemLabel = (member?: BackendMemberModel) => {
    const phoneNumber = member?.phone
      ? `${message({ id: "phoneNumber" })}: ${member?.phone}`
      : "";
    const loyaltyCardNumber = member?.loyaltyCardNumber
      ? `${message({ id: "loyaltyCardNumber" })}: ${member?.loyaltyCardNumber}`
      : "";

    return `${member?.email || ""} ${phoneNumber} ${loyaltyCardNumber} ${message(
      { id: "memberID" },
    )}: ${member?.customerId} `;
  };

  const memberItems =
    searchedMembers?.items?.map((member) => ({
      label: getMemberItemLabel(member),
      value: member?.customerId,
      shortLabel: member?.email
        ? `${member?.email} (${member?.customerId})`
        : member?.customerId,
    })) || [];

  useEffect(() => {
    if (searchedMembers) {
      setIsLoading(true);
    }
  }, [searchedMembers]);

  return (
    <>
      {field.isConditionSaved || readOnly ? (
        <>
          {message({ id: "is_one_of" }).toLocaleLowerCase()}{" "}
          {formData
            ?.slice(0, maxVisibleConditionDataCount)
            ?.map((member: string, index: number) => (
              <div className={classes.box} key={index} data-hj-suppress>
                {memberItems.find((item) => item.value === member)
                  ?.shortLabel || member}
              </div>
            ))}
          <MoreElements
            totalItemsNumber={formData?.length}
            tooltipData={{
              type: "labels",
              data: formData
                ?.slice(maxVisibleConditionDataCount)
                ?.map(
                  (customerId: string) =>
                    memberItems.find((member) => member.value === customerId)
                      ?.shortLabel,
                ),
            }}
          />
        </>
      ) : (
        <>
          {formData?.map((item: string, index: number) => (
            <MemberFetcher
              id={item}
              key={index}
              setListOfFetchedMembers={(data) => {
                setListOfFetchedMembers((previousArrayState) => [
                  ...previousArrayState,
                  data,
                ]);
              }}
            />
          ))}
          <p className={classes.customMemberListInfo}>
            {message({ id: "selectMemberInfo" })}
          </p>
          <Field name={fieldName}>
            {({ form, field }: { form: any; field: any }) =>
              field?.value &&
              isLoading && (
                <CustomMemberListSelect
                  listOfMembers={searchedMembers?.items}
                  formField={field}
                  items={memberItems}
                  className={`${classes.input} ${classes.fullWidth}`}
                  label={message({ id: "customMembersList" })}
                  onBlur={() => setSearchedMemberValue("")}
                  onChange={(newValue: LabelValuePair[]) => {
                    const mappedField = newValue.map(
                      (item: LabelValuePair) => item.value,
                    );
                    form.setFieldValue(fieldName, mappedField);
                    setSearchedMemberValue("");
                  }}
                  onTyping={(newValue: string) =>
                    setSearchedMemberValue(newValue)
                  }
                  placeholder={message({ id: "selectMemberInfo" })}
                  defaultValue={field.value.map((customerId: string) => {
                    const foundMember =
                      listOfFetchedMembers &&
                      listOfFetchedMembers.find(
                        (member) => member.customerId === customerId,
                      );
                    return {
                      label: getMemberItemLabel(foundMember),
                      value: foundMember?.customerId,
                    };
                  })}
                  data-test-id="segment-conditions-custom-members-list"
                />
              )
            }
          </Field>
        </>
      )}
    </>
  );
}
